import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  withStyles,
  Pagination,
  AccountBalance,
  useUpdateQueryParams,
  formattedCurrency,
  getCurrencySign,
} from 'vopay';
import { BodyText, Button, Title, Table as UITable } from 'UI';
import { TableFiltersContainer } from 'vopay';
import Table, { ColConfig, createTableProps, Row } from './Table/Table';
import AccountBreadcrumbs from './AccountBreadcrumbs/AccountBreadcrumbs';
import AccountDetailsDrawer from './AccountDetailsDrawer/AccountDetailsDrawer';
import AccountStatus from './AccountStatus/AccountStatus';
import TransferDrawer from './Transfer/TransferDrawer';
import derivePartnerAccountStatus from './partnerAccountStatus';

import { useEffect, useState, useMemo } from 'react';

import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { usePagination, useDateLocale, Skeleton } from 'vopay';

import AccountSearchForm from './AccountSearchForm';
import PartnerAccountCreation from './AccountCreation/PartnerAccountCreation/PartnerAccountCreation';
import AccountsOverview from './AccountsOverview/AccountsOverview';
import { getAccountStatusTiles } from './AccountsOverview/onboardingPipeline';
import useFetchPartnerAccountStatusSummary from '@/components/Entities/hooks/useApi/useFetchPartnerAccountStatusSummary';
import { formatApiDate } from './accountStatusDate';
import accountPageLocation from './accountPageLocation';
import { useTranslation } from 'react-i18next';
import styles from './AccountsList.module.scss';
import useFetchPartnerAccounts, {
  type FetchPartnerAccountsResponse,
  toPartnerAccountStatusFilter,
} from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import useCountryLabels from '@/hooks/useCountryLabels';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

const classes = withStyles(styles);

/** Rows per page, sent with the request and used to size the paginator. */
const PAGE_SIZE = 20;

type AccountsListProps = {
  onRouteAccess: () => void;
  /** Exports the current filters; an account name narrows the export to that one account. */
  exportCSV: (accountName?: string) => void;
};

type QueryParams = {
  country: string;
  status: string;
  searchText: string;
  currency: string;
};

export default function AccountsList({ onRouteAccess, exportCSV }: AccountsListProps) {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List' });
  const { t: tOverview } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const dateLocale = useDateLocale();
  const { resolveCountryLabel } = useCountryLabels();

  const {
    response: { isRoleViewOnly, accountId: loggedInAccountId, isInsideSales, partnerTypeLabel },
  } = usePartnerData();

  const { permissions } = usePermissions();

  const { canCreateAccount } = useModuleFeatureFlag('accounts');
  const canTransfer = permissions.AccountTransfersEnabled;
  const location = useLocation();

  const {
    response: fetchPartnerAccountsResponse,
    fetching: fetchingAccounts,
    fetchPartnerAccounts,
    reset,
  } = useFetchPartnerAccounts();

  const toast = useToast();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // The status summary feeds the overview's stage tiles and, through them, the Status filter's
  // options — one list for both (ID-13964 D3), so it is fetched here rather than inside the overview.
  const {
    fetching: fetchingStatusSummary,
    numTotalAccounts,
    statusCounts,
    fetchPartnerAccountStatusSummary,
  } = useFetchPartnerAccountStatusSummary();

  // The hook's `fetching` is false until the mount effect below runs; without this the overview would
  // flash zeroed totals for one frame first.
  const [hasLoadedStatusSummary, setHasLoadedStatusSummary] = useState(false);

  // Fetched once on mount; the summary is independent of the list's filters and pagination.
  useEffect(() => {
    fetchPartnerAccountStatusSummary()
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          tOverview('loadError', {
            defaultValue: 'Could not load the accounts overview: {{errorMessage}}',
            errorMessage,
          }),
        );
      })
      .finally(() => setHasLoadedStatusSummary(true));
  }, []);

  const statusTiles = useMemo(
    () => getAccountStatusTiles(partnerTypeLabel, permissions.partnerLiability, statusCounts),
    [partnerTypeLabel, permissions.partnerLiability, statusCounts],
  );

  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountBalance | null>(null);
  // Null keeps the transfer drawer closed; a row's Transfer button names the account to debit, the
  // header's starts blank.
  const [transferRequest, setTransferRequest] = useState<{ fromAccount?: AccountBalance } | null>(null);
  // The hook's `fetching` is false until the mount effect below runs; without this the page would
  // render its resolved empty state for one frame first.
  const [hasLoaded, setHasLoaded] = useState(false);

  // The partner's program terms ride along on every page of the listing, but they are partner-level and
  // do not change with the filters — so they are kept from the first answer, and the card is not put
  // back into its loading state on every filter, page or tile click.
  const [programTerms, setProgramTerms] = useState<Pick<
    FetchPartnerAccountsResponse,
    'RevShare' | 'MinimumProgramFee'
  > | null>(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const status = useMemo(() => toPartnerAccountStatusFilter(queryParams.get('status')), [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  // "All" is a selection, not a filter, and the query string keeps it as one — so both flags below
  // have to discount it or the UI reports filters nobody applied.
  const isFiltered = (value: string) => value !== '' && value.toLocaleLowerCase() !== 'all';

  // Active alone keeps the nested tree: it is the list's resting state, not a search.
  const hasSearch = useMemo(
    () =>
      (isFiltered(status) && status !== 'Active') || isFiltered(country) || isFiltered(currency) || searchText !== '',
    [status, country, currency, searchText],
  );

  const filtersApplied = useMemo(
    () => isFiltered(status) || isFiltered(country) || isFiltered(currency) || searchText !== '',
    [status, country, currency, searchText],
  );

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(
    fetchPartnerAccountsResponse?.PaginationMetaData?.TotalRecords || 0,
    {
      pageSize: PAGE_SIZE,
    },
  );

  useEffect(() => {
    onRouteAccess();
  }, [location]);

  useEffect(() => {
    setBalances(fetchPartnerAccountsResponse?.balances ?? []);
  }, [fetchPartnerAccountsResponse]);

  // The page's own query; also what a created account or a completed transfer re-runs so the rows
  // and balances on screen are current.
  const loadAccounts = () =>
    fetchPartnerAccounts({
      Country: country,
      VopayAccountStatus: status,
      SearchText: searchText,
      Currency: currency,
      Limit: PAGE_SIZE,
      Offset: offset,
    })
      .then(({ RevShare, MinimumProgramFee }) => {
        setProgramTerms((current) => current ?? { RevShare, MinimumProgramFee });
      })
      .catch((error) => {
        // Without this the failure is indistinguishable from "this partner has no accounts". The hook
        // has already logged the cause; the user needs to know the list is wrong, not just empty, and
        // the reason it gives support something to act on.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // Clearing the rows alone would leave the previous response feeding the paginator and the
        // program-terms figures, so an empty table would sit beside a page count from the last query.
        reset();
        setBalances([]);
        toast.neutral(t('loadError', { defaultValue: 'Could not load your accounts: {{errorMessage}}', errorMessage }));
      })
      .finally(() => setHasLoaded(true));

  useEffect(() => {
    loadAccounts();
  }, [country, status, searchText, currency, offset]);

  /**
   * earthnode applies the status filter to the top-level rows only — nested sub-accounts arrive from
   * a separate, unfiltered call — so the one filter that keeps the tree (Active) would otherwise show
   * Pending and Disabled sub-accounts underneath Active parents. Status is the only filter this has
   * to answer for: every other one flattens the table, and the sub-rows are never built.
   */
  const matchesStatusFilter = (account: AccountBalance) => !isFiltered(status) || account.AccountStatus === status;

  //one mapper. The flat (search) table is the nested one with the sub-rows left off, which is
  // what removes the tree and makes the breadcrumbs the only way back up it.
  function toRows(accounts: AccountBalance[], { nested }: { nested: boolean }): Row<AccountBalance>[] {
    return accounts.map((account) => {
      const subAccounts = (account.SubAccounts || []).filter(matchesStatusFilter);

      return {
        id: `${account.AccountID}-${account.Currency}`,
        data: account,
        subRows: nested && subAccounts.length > 0 ? toRows(subAccounts, { nested }) : undefined,
      };
    });
  }

  const renderPartnerName = ({ data }: { data: AccountBalance }) => {
    if (data.PartnerAccountID && data.PartnerAccountID === loggedInAccountId) {
      return (
        <>
          {data.PartnerAccountName} • {t('Table.direct', { defaultValue: 'Direct' })}
        </>
      );
    }

    return data.PartnerAccountID ? (
      <UITable.Link to={{ pathname: `/partners/${data.PartnerAccountID}` }}>{data.PartnerAccountName}</UITable.Link>
    ) : (
      <>{data.PartnerAccountName}</>
    );
  };

  const renderStatus = ({ data }: { data: AccountBalance }) => <AccountStatus account={data} />;

  // Account Name opens the details drawer (spec 5.3); the full page is a click further, from the drawer.
  const renderAccountLink = (data: AccountBalance) => (
    <UITable.LinkButton onClick={() => setSelectedAccount(data)}>{data.AccountName}</UITable.LinkButton>
  );

  // The nested and the flat (search) tables share every column but Account Name, which carries
  // breadcrumbs in the flat one. Spec 5.3 column order.
  function buildColumns(
    accountName: (row: { data: AccountBalance }) => React.ReactNode,
  ): ColConfig<AccountBalance, string>[] {
    return [
      {
        id: 'dateAdded',
        header: t('Table.dateAdded', { defaultValue: 'Date Added' }),
        align: 'left',
        template: ({ data }) => (
          <span className={classes('dateCell')}>{formatApiDate(data.DateAdded, dateLocale)}</span>
        ),
      },
      {
        id: 'accountName',
        header: t('Table.accountName'),
        align: 'left',
        template: accountName,
      },
      {
        id: 'partnerAccountName',
        header: t('Table.partnerAccountName'),
        align: 'left',
        visible: permissions.PartnerCreationEnabled,
        template: renderPartnerName,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: renderStatus,
      },
      {
        id: 'country',
        header: t('Table.country'),
        align: 'left',
        template: ({ data }) => resolveCountryLabel(data.Country),
      },
      {
        id: 'pending',
        header: t('Table.pending'),
        align: 'left',
        template: ({ data }) =>
          `${data.Currency} ${getCurrencySign(data.Currency)}${formattedCurrency(data.PendingBalance)}`,
      },
      {
        id: 'available',
        header: t('Table.available'),
        align: 'left',
        template: ({ data }) =>
          `${data.Currency} ${getCurrencySign(data.Currency)}${formattedCurrency(data.AvailableBalance)}`,
      },
      // Transfers debit an Active account — the status the badge shows, so a Blocked account (still
      // IsActive underneath) gets no button — and only a partner who may transfer at all sees the
      // column; otherwise it would never hold anything.
      {
        id: 'actions',
        header: t('Table.actions', { defaultValue: 'Actions' }),
        align: 'left',
        visible: canTransfer,
        template: ({ data }) =>
          derivePartnerAccountStatus(data) === 'Active' ? (
            <Button
              id={`accounts-transfer-${data.AccountID}-${data.Currency}`}
              size="small"
              disabled={isRoleViewOnly}
              onClick={() => setTransferRequest({ fromAccount: data })}
            >
              {t('transferButton', { defaultValue: 'Transfer' })}
            </Button>
          ) : null,
        hasDivider: false,
      },
    ];
  }

  const tableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: true,
    rows: toRows(balances, { nested: true }),
    columns: buildColumns(({ data }) => (
      <div className={classes('accountNameCell')}>
        {renderAccountLink(data)}

        {data.DBAName && (
          <>
            <br />
            <i>{data.DBAName}</i>
          </>
        )}
      </div>
    )),
  });

  const searchTableProps = createTableProps({
    id: 'accountsList',
    hasPlaceholderArrows: false,
    rows: toRows(balances, { nested: false }),
    columns: buildColumns(({ data }) => {
      const onClick = (value: string) => {
        // When clicking breadcrumbs, update query params to search for that account
        setPageIndex(0);

        updateQueryParams({
          searchText: value,
          country: '',
          currency: '',
          status: '',
        });
      };

      return (
        <Box flx="col" ai="fs">
          <AccountBreadcrumbs
            parentAccounts={data.ParentAccounts}
            onCrumbClick={(parent) => onClick(parent.AccountName)}
          />

          {renderAccountLink(data)}
          {data.DBAName && <i>{data.DBAName}</i>}
        </Box>
      );
    }),
  });

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        {/* Figma: H1/Large Title 32/Emphasized + Body/16/Regular (Text/Secondary75) */}
        <div className={classes('pageHeading')}>
          <Title>{t('title')}</Title>

          <BodyText className={classes('pageDescription')}>
            {t('pageDescription', {
              defaultValue: 'All accounts and sub-accounts in your book, created by you or your partners.',
            })}
          </BodyText>
        </div>

        {/* Figma `Buttons`: Size="Large"; Export CSV Type="Secondary", Add Account Type="Primary" + Left Indicator="add" */}
        <div className={classes('headerActions')}>
          <Button
            id="accounts-ExportCSV"
            size="large"
            disabled={isRoleViewOnly}
            onClick={() => {
              exportCSV();
            }}
          >
            {t('exportCSVButton')}
          </Button>

          {canTransfer && (
            <Button
              id="accounts-transfer"
              size="large"
              disabled={isRoleViewOnly}
              onClick={() => setTransferRequest({})}
            >
              {t('transferButton', { defaultValue: 'Transfer' })}
            </Button>
          )}

          {canCreateAccount && (
            <Button
              id="accounts-addAccount"
              colorScheme="primary"
              size="large"
              iconLeft="add"
              disabled={isRoleViewOnly}
              onClick={() => (isInsideSales ? history.push(`${ACCOUNTS_PATH}/create`) : setIsCreateDrawerOpen(true))}
            >
              {t('addAccountButton')}
            </Button>
          )}
        </div>
      </Box>

      <AccountsOverview
        // The listing carries the partner's program terms, so the card reads them off this response.
        fetchingProgramTerms={!programTerms && !hasLoaded}
        revShare={programTerms?.RevShare ?? null}
        minimumProgramFee={programTerms?.MinimumProgramFee ?? null}
        fetchingStatusSummary={fetchingStatusSummary || !hasLoadedStatusSummary}
        numTotalAccounts={numTotalAccounts}
        statusCounts={statusCounts}
        stages={statusTiles}
        selectedStatus={status}
        // A tile is the Status filter set to that status, and clicking the selected one again clears it
        // (ID-13981); the other filters stay as they are.
        onStatusClick={(clickedStatus) => {
          setPageIndex(0);
          updateQueryParams({ country, currency, searchText, status: clickedStatus === status ? '' : clickedStatus });
        }}
      />

      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied || !!searchText}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <AccountSearchForm
              country={country}
              status={status}
              searchText={searchText}
              currency={currency}
              statuses={statusTiles}
              onAccountSearch={(values) => {
                setPageIndex(0);

                updateQueryParams({
                  country: values.Country || '',
                  status: values.VopayAccountStatus || '',
                  searchText: values.SearchText || '',
                  currency: values.Currency || '',
                });
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Box className={classes('accountsTableContainer', 'scrollbar')}>
          {/* The Box is a flex row, so the skeleton's inline container needs a width of its own. */}
          {(fetchingAccounts || !hasLoaded) && <Skeleton height={320} containerClassName={classes('tableSkeleton')} />}
          {!fetchingAccounts && hasLoaded && !hasSearch && <Table {...tableProps} />}
          {!fetchingAccounts && hasLoaded && hasSearch && <Table {...searchTableProps} />}
        </Box>

        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                setPageIndex(e.selected);
              }}
            />
          )}
        </Box>
      </Card>

      {isCreateDrawerOpen && (
        <PartnerAccountCreation onClose={() => setIsCreateDrawerOpen(false)} onCreated={loadAccounts} />
      )}

      <AccountDetailsDrawer
        account={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        onViewFullDetails={(account) => history.push(accountPageLocation(account))}
      />

      <TransferDrawer
        isOpen={transferRequest !== null}
        fromAccount={transferRequest?.fromAccount}
        onClose={() => setTransferRequest(null)}
        onTransferred={() => {
          setTransferRequest(null);
          loadAccounts();
        }}
      />
    </Box>
  );
}
